<template>
  <v-container fluid class="fill-height bg-primary">
    <v-row justify="center" align="center">
      <v-col cols="12" sm="10" md="8" lg="6">
        <v-card class="pa-4" elevation="12">
          <v-card-title class="text-center text-h5 mb-4">
            <v-icon size="48" color="primary" class="mb-2">mdi-account-cog</v-icon>
            <div>{{ t.onboarding.title }}</div>
            <div class="text-caption text-grey">{{ t.onboarding.step_of.replace('{step}', step).replace('{total}', '2') }}</div>
          </v-card-title>

          <v-card-text>
            <!-- Step 1: Choose PF or PJ -->
            <div v-if="step === 1">
              <div class="text-subtitle-1 mb-4 text-center">{{ t.onboarding.account_type_question }}</div>

              <v-row class="mb-4">
                <v-col cols="6">
                  <v-card
                    :color="legalType === 1 ? 'primary' : ''"
                    :variant="legalType === 1 ? 'flat' : 'outlined'"
                    class="pa-4 text-center cursor-pointer"
                    @click="legalType = 1"
                    hover
                  >
                    <v-icon size="48" :color="legalType === 1 ? 'white' : 'primary'" class="mb-2">mdi-account</v-icon>
                    <div :class="legalType === 1 ? 'text-white' : ''" class="font-weight-bold">{{ t.onboarding.individual }}</div>
                    <div :class="legalType === 1 ? 'text-white' : 'text-grey'" class="text-caption">{{ t.onboarding.individual_desc }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card
                    :color="legalType === 2 ? 'primary' : ''"
                    :variant="legalType === 2 ? 'flat' : 'outlined'"
                    class="pa-4 text-center cursor-pointer"
                    @click="legalType = 2"
                    hover
                  >
                    <v-icon size="48" :color="legalType === 2 ? 'white' : 'primary'" class="mb-2">mdi-domain</v-icon>
                    <div :class="legalType === 2 ? 'text-white' : ''" class="font-weight-bold">{{ t.onboarding.company }}</div>
                    <div :class="legalType === 2 ? 'text-white' : 'text-grey'" class="text-caption">{{ t.onboarding.company_desc }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <v-btn
                color="primary"
                variant="flat"
                block
                size="large"
                :disabled="legalType === null"
                @click="step = 2"
              >
                {{ t.onboarding.continue }}
                <v-icon class="ml-1">mdi-arrow-right</v-icon>
              </v-btn>
            </div>

            <!-- Step 2: Fill in details -->
            <div v-if="step === 2">
              <v-btn variant="text" size="small" @click="step = 1" class="mb-3">
                <v-icon class="mr-1">mdi-arrow-left</v-icon> {{ t.onboarding.back }}
              </v-btn>

              <v-form ref="onboardingForm" @submit.prevent="submitOnboarding">
                <!-- PF Fields -->
                <v-text-field
                  v-model="clientName"
                  :label="legalType === 1 ? t.onboarding.full_name : t.onboarding.contact_name"
                  prepend-inner-icon="mdi-account"
                  :rules="[v => !!v && v.trim().length > 0 || t.onboarding.name_required]"
                  required
                  variant="outlined"
                  class="mb-3"
                  :disabled="loading"
                />

                <v-text-field
                  v-model="phone"
                  :label="t.onboarding.phone_optional"
                  prepend-inner-icon="mdi-phone"
                  variant="outlined"
                  class="mb-3"
                  :disabled="loading"
                />

                <!-- PJ Additional Fields -->
                <template v-if="legalType === 2">
                  <v-divider class="my-3" />
                  <div class="text-subtitle-2 mb-3">{{ t.onboarding.company_data }}</div>

                  <v-text-field
                    v-model="companyName"
                    :label="t.onboarding.company_name"
                    prepend-inner-icon="mdi-domain"
                    :rules="[v => !!v && v.trim().length > 0 || t.onboarding.company_name_required]"
                    required
                    variant="outlined"
                    class="mb-3"
                    :disabled="loading"
                  />

                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="cui"
                        :label="t.onboarding.cui_cif"
                        prepend-inner-icon="mdi-identifier"
                        variant="outlined"
                        :disabled="loading"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        v-model="registrationNumber"
                        :label="t.onboarding.registration_number"
                        prepend-inner-icon="mdi-file-document"
                        variant="outlined"
                        :disabled="loading"
                      />
                    </v-col>
                  </v-row>

                  <v-text-field
                    v-model="bank"
                    :label="t.onboarding.bank_optional"
                    prepend-inner-icon="mdi-bank"
                    variant="outlined"
                    class="mb-3"
                    :disabled="loading"
                  />

                  <v-text-field
                    v-model="iban"
                    :label="t.onboarding.iban_optional"
                    prepend-inner-icon="mdi-credit-card"
                    variant="outlined"
                    class="mb-3"
                    :disabled="loading"
                  />
                </template>

                <v-btn
                  color="primary"
                  variant="flat"
                  block
                  size="large"
                  type="submit"
                  :loading="loading"
                  class="mt-4"
                >
                  <v-icon class="mr-1">mdi-check</v-icon>
                  {{ t.onboarding.finish }}
                </v-btn>
              </v-form>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000" location="top">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue'
import { log } from "@/composables/useLog"
import { t as tRef } from "@/composables/useLanguages"
import { completeOnboarding } from "@/composables/useOnboardingAPI"
import store from "@/store"
import { landingPathForUser } from "@/composables/useClientLanding"

export default defineComponent({
  name: 'OnboardingView',
  data() {
    return {
      step: 1,
      legalType: null, // 1=PF, 2=PJ
      clientName: '',
      phone: '',
      companyName: '',
      cui: '',
      registrationNumber: '',
      bank: '',
      iban: '',
      loading: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    t() {
      return tRef.value
    }
  },
  mounted() {
    // Pre-fill with existing data if available
    const user = store.getters.getUser
    if (user) {
      if (user.name && user.name !== user.email) {
        this.clientName = user.name
      }
      if (user.phone) {
        this.phone = user.phone
      }
    }
  },
  methods: {
    async submitOnboarding() {
      const { valid } = await this.$refs.onboardingForm.validate()
      if (!valid) return

      this.loading = true
      try {
        const token = store.getters.getToken
        const data = {
          legal_type: this.legalType,
          name: this.clientName.trim()
        }

        if (this.phone) data.phone = this.phone.trim()

        if (this.legalType === 2) {
          data.company_name = this.companyName.trim()
          data.cui = this.cui.trim()
          data.registration_number = this.registrationNumber.trim()
          data.bank = this.bank.trim()
          data.iban = this.iban.trim()
        }

        const response = await completeOnboarding(token, data)

        if (response.error) {
          this.snackbarMessage = response.message || this.t.common.error
          this.snackbarColor = 'error'
          this.showSnackbar = true
        } else {
          // Update store with new client data
          const user = store.getters.getUser
          if (user) {
            if (user.userClient) {
              user.userClient.onboarding_completed = true
              user.userClient.name = this.clientName.trim()
            }
            user.name = this.clientName.trim()
            user.client_name = this.clientName.trim()
            store.commit('setUser', { user: user })
            // Also update sessionStorage if it has user data
            const sessionUser = sessionStorage.getItem('user')
            if (sessionUser) {
              sessionStorage.setItem('user', JSON.stringify(user))
            }
          }

          this.snackbarMessage = this.t.onboarding.profile_completed
          this.snackbarColor = 'success'
          this.showSnackbar = true

          // Redirect: simple end-users go to the new client shell, others to dashboard
          setTimeout(() => {
            this.$router.push(landingPathForUser(store.getters.getUser))
          }, 1000)
        }
      } catch (err) {
        log("Error completing onboarding:")
        log(err)
        this.snackbarMessage = this.t.onboarding.error_completing_profile
        this.snackbarColor = 'error'
        this.showSnackbar = true
      } finally {
        this.loading = false
      }
    }
  }
})
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
